import { html, nothing } from "lit-html";
import type { View, App } from "../app.js";
import { api } from "../api.js";
import type { Scorecards, Trend, Conversion, Opportunity } from "../types.js";
import { kpi, targetKpis, pacePill, meter, oppItem, sectionHead, delta, repPageRows } from "../components.js";
import { money, moneyShort, pct, number } from "../format.js";
import { divisionIcon } from "../icons.js";
import "../charts.js";

interface Data { sc: Scorecards; trend: Trend; conv: Conversion; opps: Opportunity[] }

export function trendChart(trend: Trend, growth: number, height = 270) {
  return html`<ff-line .data=${{
    labels: trend.months,
    format: moneyShort,
    height,
    highlightIndex: trend.partialMonth,
    series: [
      { name: `${trend.year}`, values: trend.current, color: "var(--series-1)", area: true },
      { name: `${trend.year - 1}`, values: trend.prior, color: "var(--series-muted)", muted: true },
      { name: `Target (+${Math.round(growth * 100)}%)`, values: trend.target, color: "var(--series-2)", dashed: true },
    ],
  }}></ff-line>
  <div class="legend mt">
    <span><i style="background:var(--series-1)"></i>${trend.year}</span>
    <span><i style="background:var(--series-muted)"></i>${trend.year - 1}</span>
    <span style="color:var(--series-2)"><i class="dash"></i><span style="color:var(--text-2)">Target (+${Math.round(growth * 100)}% over ${trend.year - 1})</span></span>
  </div>`;
}

export const overview: View<Data> = {
  title: () => "Overview",
  filters: ["year", "manager", "rep", "division"],
  async load(app) {
    const q = app.q();
    const [sc, trend, conv, opps] = await Promise.all([api.scorecards(q), api.trend(q), api.conversion(q), api.opportunities(q)]);
    return { sc, trend, conv, opps: opps.items };
  },
  render(app: App, d) {
    const t = d.sc.total;
    const divs = app.me.divisions.filter((x) => d.sc.byDivision[x.key]);
    const reps = repPageRows(d.sc.rows).slice(0, 12);
    return html`
      <div class="grid g-kpi">
        ${targetKpis(t, d.sc.growth)}
        ${kpi("Quote win rate", pct(d.conv.rate), html`<span>${number(d.conv.won)} won of ${number(d.conv.resolved)} decided, last 12 mo</span>`)}
        ${kpi("Open quotes", moneyShort(d.conv.openAmount), html`<span>${number(d.conv.open)} open in the last 12 months</span>`)}
      </div>

      <div class="grid g-2 mt">
        <section class="glass card">
          ${sectionHead("Monthly revenue", `${d.trend.year} vs ${d.trend.year - 1} and target`)}
          ${trendChart(d.trend, d.sc.growth, 340)}
        </section>
        <section class="glass card">
          ${sectionHead("By division", "Year to date vs target", html`<a class="link" href=${app.href("divisions")}>All divisions</a>`)}
          <div class="panel-list">
            ${divs.length ? divs.map((dv) => {
              const tt = d.sc.byDivision[dv.key]!;
              return html`<a class="opp" href=${app.href("divisions", dv.key)} style="grid-template-columns:auto 1fr auto">
                <div class="div-icon">${divisionIcon[dv.key] ?? nothing}</div>
                <div style="min-width:0"><div class="opp-title">${dv.name}</div><div class="opp-detail">${money(tt.ytd)} · ${delta(tt.ytd, tt.priorYtd)} vs last year</div>
                  <div style="margin-top:8px">${meter(tt)}</div></div>
                <div>${pacePill(tt.pace, true)}</div>
              </a>`;
            }) : html`<div class="empty-state">No sales in this view yet.</div>`}
          </div>
        </section>
      </div>

      <div class="grid g-2 mt">
        <section class="glass card">
          ${sectionHead("Reps", "Ranked by revenue this year", html`<a class="link" href=${app.href("reps")}>All reps</a>`)}
          ${reps.length ? html`<div class="table-wrap"><table class="t">
            <thead><tr><th>Rep</th><th class="r">YTD</th><th class="r hide-sm">Target</th><th class="r">GP %</th><th class="hide-sm">Progress</th><th class="r">Pace</th></tr></thead>
            <tbody>${reps.map((r) => html`<tr class="click" @click=${() => app.go("reps", String(r.repId))}>
              <td class="name">${r.repName}</td>
              <td class="r">${moneyShort(r.total.ytd)}</td>
              <td class="r muted hide-sm">${moneyShort(r.total.target)}</td>
              <td class="r" title=${r.gp == null ? "" : `GP ${moneyShort(r.gp)}`}>${pct(r.gpPct, 1)}</td>
              <td class="meter-cell hide-sm">${meter(r.total)}</td>
              <td class="r">${pacePill(r.total.pace, true)}</td>
            </tr>`)}</tbody></table></div>` : html`<div class="empty-state">No rep sales in this view.</div>`}
        </section>
        <section class="glass card">
          ${sectionHead("Bring these up", `${d.opps.length} opportunities`, html`<a class="link" href=${app.href("opportunities")}>See all</a>`)}
          ${d.opps.length ? d.opps.slice(0, 6).map((o) => oppItem(o, (f) => app.go("map", f))) : html`<div class="empty-state">Nothing flagged right now.</div>`}
        </section>
      </div>`;
  },
};
